import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, Pencil, Plus, Power, UserRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusPill, UsernameInput } from "@/components/common";
import { emailLocal, fmtDate, rupiah, toEmail } from "@/lib/format";

const EMPTY = { name: "", head_name: "", head_email: "", head_password: "", package_id: "none" };

const PackageSelect = ({ value, onChange }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/packages").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid="wilayah-package-select"><SelectValue placeholder="Pilih paket" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Tanpa paket (tidak dibatasi)</SelectItem>
        {items.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`package-option-${p.id}`}>{p.name} · maks. {p.cabang_limit} cabang</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const OfficeForm = ({ open, onOpenChange, initial, cfg, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(initial);

  useEffect(() => {
    if (open) setForm(initial ? { name: initial.name, head_name: initial.head?.name || "", head_email: emailLocal(initial.head?.email), head_password: "", package_id: initial.package_id || "none" } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, head_email: toEmail(form.head_email), head_password: form.head_password || (editing ? null : ""), package_id: form.package_id === "none" ? null : form.package_id };
      if (editing) await api.put(`${cfg.endpoint}/${initial.id}`, body);
      else await api.post(cfg.endpoint, body);
      toast.success(editing ? `${cfg.title} diperbarui` : `${cfg.title} ditambahkan`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={`${cfg.kind}-form-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? `Edit ${cfg.title}` : `Tambah ${cfg.title}`}</DialogTitle>
          <DialogDescription>{editing ? "Kosongkan password jika tidak ingin mengubahnya." : `Sekaligus membuat akun ${cfg.headLabel}.`}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label={`Nama ${cfg.title}`}>
            <Input required value={form.name} onChange={set("name")} placeholder={cfg.placeholder} data-testid={`${cfg.kind}-name-input`} className="h-11" />
          </Field>
          {cfg.kind === "wilayah" && <Field label="Paket Langganan" hint="Menentukan batas jumlah kantor cabang"><PackageSelect value={form.package_id} onChange={(v) => setForm((f) => ({ ...f, package_id: v }))} /></Field>}
          <div className="rounded-xl border p-4 space-y-4 bg-muted/30">
            <p className="eyebrow">Akun {cfg.headLabel}</p>
            <Field label="Nama Lengkap"><Input required value={form.head_name} onChange={set("head_name")} data-testid={`${cfg.kind}-head-name-input`} className="h-11" /></Field>
            <Field label="Username Login" hint={`Email login otomatis: ${form.head_email || "nama_akun"}@kspbam.com`}>
              <UsernameInput value={form.head_email} onChange={(v) => setForm((f) => ({ ...f, head_email: v }))} required testId={`${cfg.kind}-head-email-input`} />
            </Field>
            <Field label={editing ? "Password Baru (opsional)" : "Password"} hint="Minimal 8 karakter">
              <Input type="password" required={!editing} minLength={8} value={form.head_password} onChange={set("head_password")} data-testid={`${cfg.kind}-head-password-input`} className="h-11" />
            </Field>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="h-11 rounded-full px-6" data-testid={`${cfg.kind}-form-submit`}>{busy ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v, testId }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b last:border-0 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="font-medium text-right" data-testid={testId}>{v}</span>
  </div>
);

const OfficeDetail = ({ id, onClose, cfg }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!id) { setData(null); return; }
    api.get(`${cfg.endpoint}/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id, cfg.endpoint]);
  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${cfg.kind}-detail-dialog`}>
        <DialogHeader><DialogTitle className="font-heading">Rincian {cfg.title}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground py-6">Memuat...</p> : (
          <div>
            <Row k="Nama" v={data.name} testId="detail-office-name" />
            <Row k="Status" v={<StatusPill active={data.active} />} />
            <Row k={cfg.headLabel} v={data.head?.name || "-"} />
            <Row k="Email Login" v={data.head?.email || "-"} />
            <Row k="Dibuat" v={fmtDate(data.created_at)} />
            {cfg.kind === "wilayah" ? (
              <>
                <Row k="Paket" v={data.package ? `${data.package.name} (maks. ${data.package.cabang_limit})` : "Tanpa paket"} testId="detail-office-package" />
                <Row k="Jumlah Kantor Cabang" v={data.cabang_count} />
                {data.cabang?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="eyebrow">Kantor Cabang</p>
                    {data.cabang.map((c) => (
                      <div key={c.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm">
                        <span>{c.name}</span><StatusPill active={c.active} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <Row k="Jumlah Petugas" v={data.staff_count} />
                <Row k="Jumlah Anggota" v={data.member_count} />
                <Row k="Total Simpanan" v={rupiah(data.total_simpanan)} />
                <Row k="Pinjaman Berjalan" v={data.loan_active_count} />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const OfficeCard = ({ o, cfg, i, onDetail, onEdit, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
    className="office-card group" data-testid={`${cfg.kind}-card-${o.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <span className="h-14 w-14 rounded-2xl bg-primary/12 text-primary grid place-items-center transition-transform group-hover:scale-105">
        <cfg.icon className="h-7 w-7" />
      </span>
      <StatusPill active={o.active} testId={`${cfg.kind}-status-${o.id}`} />
    </div>
    <h3 className="font-heading text-lg font-bold mt-5 leading-snug" data-testid={`${cfg.kind}-card-name-${o.id}`}>{o.name}</h3>
    <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
      <p className="flex items-center gap-2 truncate"><UserRound className="h-4 w-4 shrink-0" />{o.head?.name || "-"}</p>
      <p className="flex items-center gap-2 truncate"><Mail className="h-4 w-4 shrink-0" />{o.head?.email || "-"}</p>
    </div>
    <p className="text-xs mt-3 font-medium" data-testid={`${cfg.kind}-card-meta-${o.id}`}>{cfg.kind === "wilayah" ? `${o.cabang_count}${o.package ? ` / ${o.package.cabang_limit}` : ""} kantor cabang · ${o.package?.name || "Tanpa paket"}` : `${o.staff_count} petugas · ${o.member_count} anggota`}</p>
    <div className="mt-5 pt-4 border-t flex items-center gap-2">
      <Button variant="outline" size="sm" className="rounded-full h-10" onClick={() => onDetail(o.id)} data-testid={`${cfg.kind}-detail-btn-${o.id}`}><Eye className="h-4 w-4 mr-1.5" />Rincian</Button>
      <Button variant="outline" size="sm" className="rounded-full h-10" onClick={() => onEdit(o)} data-testid={`${cfg.kind}-edit-btn-${o.id}`}><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
      <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
        <Power className="h-4 w-4" />
        <Switch checked={o.active} onCheckedChange={(v) => onToggle(o, v)} data-testid={`${cfg.kind}-toggle-${o.id}`} />
      </label>
    </div>
  </motion.div>
);

export const OfficeManager = ({ cfg }) => {
  const [items, setItems] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [quota, setQuota] = useState(null);

  const load = useCallback(() => {
    api.get(cfg.endpoint).then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
    if (cfg.kind === "cabang") api.get("/cabang-quota").then((r) => setQuota(r.data)).catch(() => {});
  }, [cfg.endpoint, cfg.kind]);
  const full = quota && quota.limit !== null && quota.used >= quota.limit;
  useEffect(() => { load(); }, [load]);

  const toggle = async (o, active) => {
    try {
      await api.patch(`${cfg.endpoint}/${o.id}/status`, { active });
      toast.success(`${o.name} ${active ? "diaktifkan" : "dinonaktifkan"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openAdd = () => {
    if (full) { toast.error(`Batas paket ${quota.package.name} tercapai (${quota.limit} kantor cabang). Hubungi kantor pusat untuk upgrade paket.`); return; }
    setEditing(null); setFormOpen(true);
  };

  return (
    <div>
      <PageHeader eyebrow="Worksheet" title={`Setup ${cfg.title}`} subtitle={cfg.subtitle}
        action={<Button onClick={openAdd} className="rounded-full h-11 px-6" data-testid={`add-${cfg.kind}-button`}><Plus className="h-4 w-4 mr-2" />Tambah {cfg.title}</Button>} />
      {quota && (
        <div className={`mb-5 rounded-2xl border px-4 py-3 text-sm ${full ? "border-rose-300 bg-rose-500/10" : "bg-muted/40"}`} data-testid="cabang-quota">
          Paket: <b>{quota.package?.name || "Tanpa paket"}</b> · Kantor cabang <b>{quota.used}{quota.limit !== null ? ` / ${quota.limit}` : ""}</b>{full ? " · Batas tercapai" : ""}
        </div>
      )}
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <button onClick={openAdd} className="add-card" data-testid={`add-${cfg.kind}-card`}>
            <span className="h-14 w-14 rounded-2xl border-2 border-dashed border-primary/40 text-primary grid place-items-center"><Plus className="h-7 w-7" /></span>
            <span className="font-heading font-bold mt-4">Tambah {cfg.title}</span>
            <span className="text-sm text-muted-foreground mt-1">Nama kantor + akun {cfg.headLabel}</span>
          </button>
          {items.map((o, i) => (
            <OfficeCard key={o.id} o={o} i={i} cfg={cfg} onDetail={setDetailId} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onToggle={toggle} />
          ))}
        </div>
      )}
      <OfficeForm open={formOpen} onOpenChange={setFormOpen} initial={editing} cfg={cfg} onSaved={load} />
      <OfficeDetail id={detailId} onClose={() => setDetailId(null)} cfg={cfg} />
    </div>
  );
};
